import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { useDebouncedValue } from "@/hooks/useDebouncedValue"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { ChevronsUpDown, Pencil } from "lucide-react"
import * as React from "react"
import { useArticleList } from "../../../api/articlesApi"

interface ArticlePickerProps {
  /** Label shown on the trigger — the currently chosen article, or a prompt. Ignored when variant="icon". */
  triggerLabel: string
  onSelect: (articleId: string, articleNumber: string) => void
  className?: string
  /** "icon" renders a small pencil-only trigger for swapping an already-confirmed article. */
  variant?: "button" | "icon"
}

/** Searchable single-select article combobox, filtered by article number/name server-side. */
export function ArticlePicker({
  triggerLabel,
  onSelect,
  className,
  variant = "button",
}: ArticlePickerProps) {
  const { translate } = useTranslation()
  const [open, setOpen] = React.useState(false)
  const [q, setQ] = React.useState("")
  const debouncedQ = useDebouncedValue(q, 250)

  const { data: articlePages, isFetching } = useArticleList({
    q: debouncedQ || undefined,
    limit: 50,
  })

  const articles = React.useMemo(
    () => articlePages?.pages.flatMap((p) => p.items) ?? [],
    [articlePages],
  )

  const handleSelect = React.useCallback(
    (articleId: string) => {
      const article = articles.find((a) => a.id === articleId)
      if (!article) return
      onSelect(article.id, article.articleNumber)
      setOpen(false)
      setQ("")
    },
    [articles, onSelect],
  )

  const handleOpenChange = React.useCallback((nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) setQ("")
  }, [])

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        {variant === "icon" ? (
          <button
            type="button"
            onClick={(e) => e.stopPropagation()}
            title={triggerLabel}
            aria-label={triggerLabel}
            className={cn(
              "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border text-grey5 hover:bg-primary/4 hover:text-foreground",
              className,
            )}
          >
            <Pencil size={11} />
          </button>
        ) : (
          <button
            type="button"
            onClick={(e) => e.stopPropagation()}
            className={cn(
              "inline-flex h-7 items-center gap-1 rounded-md border px-2 text-[11px] font-medium hover:bg-primary/4",
              className,
            )}
          >
            <span className="max-w-[140px] truncate">{triggerLabel}</span>
            <ChevronsUpDown size={11} className="shrink-0 opacity-50" />
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent
        className="w-64 p-0"
        align="start"
        onClick={(e) => e.stopPropagation()}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={translate("MC_QP_RECON_ARTICLE_SEARCH")}
            value={q}
            onValueChange={setQ}
          />
          <CommandList className="max-h-[260px]">
            {!isFetching && articles.length === 0 && (
              <CommandEmpty>
                {translate("MC_QP_RECON_ARTICLE_NONE")}
              </CommandEmpty>
            )}
            {articles.length > 0 && (
              <CommandGroup className="overflow-y-auto">
                {articles.map((article) => (
                  <CommandItem
                    key={article.id}
                    value={article.id}
                    onSelect={handleSelect}
                    className="text-[13px]"
                  >
                    <span className="font-mono">{article.articleNumber}</span>
                    <span className="ml-1.5 truncate text-grey5">
                      {article.name}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
